<template>
  <div class="position-relative">

    <!-- ================= SEARCH CARD ================= -->
    <b-card class="mt-4">
      <div class="mb-4" style="max-width: 400px;">
        <label class="font-weight-bold">
          Search by Email / Mobile / Name / Admission No:
        </label>

        <div class="input-group">
          <input v-model="searchKey" type="text" class="form-control"
            placeholder="Enter Email / Mobile / Name / Admission No" @keyup.enter="retrieveStudentLedger" />
          <span class="input-group-text cursor-pointer" @click="retrieveStudentLedger">
            <i class="fas fa-search text-primary"></i>
          </span>
        </div>
      </div>
    </b-card>

    <!-- ================= LEDGER VIEW ================= -->
    <div v-if="hasLedger">

      <!-- ===== LATEST LEDGER ===== -->
      <div class="border rounded p-4 bg-white shadow-sm mb-4">

        <!-- ===== DUE SUMMARY ===== -->
        <div class="alert font-weight-bold text-center mb-4"
          :class="calculateDueAmount > 0 ? 'alert-danger' : 'alert-success'">
          <span v-if="calculateDueAmount > 0">
            DUE AMOUNT: {{ formatAmount(calculateDueAmount) }}
          </span>
          <span v-else>
            NO DUES PENDING ✅
          </span>
        </div>

        <!-- ===== STUDENT INFO ===== -->
        <div class="mb-4">
          <h5 class="border-bottom pb-2">Student Information</h5>

          <div class="row" v-if="studentInfo">
            <div class="col-md-6"><strong>Full Name:</strong> {{ studentInfo.fullName }}</div>
            <div class="col-md-6"><strong>Admission No:</strong> {{ studentInfo.admissionNo }}</div>
            <div class="col-md-6"><strong>Fee Structure:</strong> {{ studentInfo.feeCategory }}</div>
            <div class="col-md-6"><strong>Academic Year:</strong> {{ studentInfo.academic }}</div>
            <div class="col-md-6"><strong>Email:</strong> {{ studentInfo.email }}</div>
            <div class="col-md-6"><strong>Mobile:</strong> {{ studentInfo.mobile }}</div>
          </div>
        </div>

        <h5 class="text-success">
          Latest Ledger #{{ latestLedger.ledger_number }}
        </h5>

        <div class="row">

          <!-- ===== FEE DETAILS ===== -->
          <div class="col-md-7">
            <h5 class="border-bottom pb-2 mb-3 font-weight-bold">Fee Details</h5>

            <table class="table table-sm table-bordered bg-white">
              <thead class="thead-light">
                <tr>
                  <th>Component</th>
                  <th class="text-right">Debit (₹)</th>
                  <th class="text-right">Credit (₹)</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(group, id) in groupedLatestLedger" :key="id">
                  <td>{{ group[0].fee_component.name }}</td>
                  <td class="text-right">{{ formatAmount(getTotal(group, 'debit_amount')) }}</td>
                  <td class="text-right">{{ formatAmount(getTotal(group, 'credit_amount')) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- ===== COLLECTION SUMMARY ===== -->
          <div class="col-md-5" v-if="latestLedger.fee_collections.length">
            <div class="mt-md-4 mt-3 p-3 border rounded bg-light">
              <h6 class="mb-3 text-success">Collection Details</h6>
              <p><strong>Collected On:</strong> {{ formatDate(latestLedger.fee_collections[0].collected_date) }}</p>
              <p><strong>Collected By:</strong> {{ latestLedger.fee_collections[0].collected_by ?
                latestLedger.fee_collections[0].collected_by.first_name : 'Online (Self-service)' }}</p>
              <p><strong>Payment Mode:</strong> {{ getPaymentModeText(latestLedger.fee_collections[0].payment_mode) }}
              </p>
              <p><strong>Fee Collected:</strong> {{ formatAmount(latestLedger.fee_collections[0].fee_collected) }}</p>
              <p class="mb-0"><strong>Balance:</strong> {{ formatAmount(latestLedger.fee_collections[0].balance_amount)
              }}</p>
              <p class="mb-0 mt-2">
                <a href="#" class="small" @click.prevent="downloadReceipt(latestLedger.fee_collections[0].id)">
                  <i class="fas fa-file-pdf mr-1"></i>Download Receipt
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== PREVIOUS LEDGERS ===== -->
      <div v-if="ledgerList.length > 1" class="mb-3 text-right">
        <a href="#" @click.prevent="showAllLedgers = !showAllLedgers" class="font-weight-bold">
          {{ showAllLedgers ? 'Hide Previous Ledgers' : 'Show All Ledgers' }}
        </a>
      </div>

      <div v-if="showAllLedgers">
        <div v-for="ledger in ledgerList.slice(1)" :key="ledger.ledger_number" class="ledger-box">
          <h5 class="text-muted">Ledger #{{ ledger.ledger_number }}</h5>

          <div class="row">
            <div class="col-md-7">
              <table class="table table-sm table-bordered bg-white">
                <tbody>
                  <tr v-for="(group, id) in getGroupedTransactions(ledger)" :key="id">
                    <td>{{ group[0].fee_component.name }}</td>
                    <td class="text-right">{{ formatAmount(getTotal(group, 'debit_amount')) }}</td>
                    <td class="text-right">{{ formatAmount(getTotal(group, 'credit_amount')) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="col-md-5" v-if="ledger.fee_collections.length">
              <ul class="list-unstyled">
                <li v-for="c in ledger.fee_collections" :key="c.id" class="mb-3 p-3 border rounded bg-light">
                  <p><strong>Collected On:</strong> {{ formatDate(c.collected_date) }}</p>
                  <p><strong>Collected By:</strong> {{ c.collected_by ? c.collected_by.first_name : 'Online (Self-service)' }}</p>
                  <p><strong>Payment Mode:</strong> {{ getPaymentModeText(c.payment_mode) }}
                  </p>
                  <p><strong>Fee Collected:</strong> {{ formatAmount(c.fee_collected) }}</p>
                  <p class="mb-0"><strong>Balance:</strong> {{ formatAmount(c.balance_amount)
                    }}</p>
                  <p class="mb-0 mt-2">
                    <a href="#" class="small" @click.prevent="downloadReceipt(c.id)">
                      <i class="fas fa-file-pdf mr-1"></i>Download Receipt
                    </a>
                  </p>

                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ================= STUDENT LIST ================= -->
    <div v-else-if="hasStudents">
      <h5 class="border-bottom pb-2 mb-3 font-weight-bold">Students</h5>

      <table class="table table-sm table-bordered bg-white">
        <thead class="thead-light">
          <tr>
            <th>Sl No</th>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Admission No</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(s, i) in students" :key="s.id">
            <td>{{ i + 1 }}</td>
            <td>{{ s.name }}</td>
            <td>{{ s.email }}</td>
            <td>{{ s.mobile }}</td>
            <td>{{ s.admission_no }}</td>
            <td>
              <b-button size="sm" variant="primary" @click="viewStudent(s)">
                View
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ================= NO LEDGER ================= -->
    <div
      v-if="showNoLedger"
      class="alert alert-danger mt-3 text-center"
    >
      No ledger found for the student.
    </div>

  </div>
</template>

<script>
export default {
  data() {
    return {
      students: [],
      ledgerList: [],
      searchKey: '',
      isLedgerNotFound: false,
      isLoading: false,
      showAllLedgers: false,
      paymentModes: [
        { value: '1', text: 'Cash' },
        { value: '2', text: 'Bank Transfer' },
        { value: '3', text: 'Cheque' },
        { value: '4', text: 'DD' },
        { value: '5', text: 'POS' },
        { value: '6', text: 'RTGS' },
        { value: '7', text: 'NEFT' },
        { value: '8', text: 'UPI' },
        { value: '9', text: 'Bank Loan' },
        { value: '10', text: 'Wallet' },
        { value: '11', text: 'Online' },
        { value: '12', text: 'Fee Credit' }
      ]
    }
  },

  computed: {
    showNoLedger() {
      return (
        !this.hasLedger &&
        !this.hasStudents &&
        this.isLedgerNotFound
      )
    },
    hasLedger() {
      return this.ledgerList.length > 0
    },
    hasStudents() {
      return this.students.length > 0
    },
    latestLedger() {
      return this.ledgerList[0] || {}
    },
    groupedLatestLedger() {
      return this.getGroupedTransactions(this.latestLedger)
    },
    studentInfo() {
      const map = this.latestLedger?.fee_transactions?.[0]?.fee_student_category_mapping
      if (!map) return null

      return {
        fullName: `${map.institutionuser.first_name} ${map.institutionuser.last_name}`,
        admissionNo: map.institutionuser.admission_no,
        email: map.institutionuser.email,
        mobile: map.institutionuser.mobile,
        feeCategory: map.feecategory.name,
        academic: `${map.feecategory.academic_year} - ${map.feecategory.academic_semester}`
      }
    },
    calculateDueAmount() {
      let debit = 0
      let credit = 0

      Object.values(this.groupedLatestLedger).forEach(group => {
        debit += this.getTotal(group, 'debit_amount')
        credit += this.getTotal(group, 'credit_amount')
      })

      return debit - credit
    }
  },

  methods: {
    async viewStudent(student) {
      this.resetState()
      this.isLoading = true

      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/studentcategorymappings/view-ledger`
        const res = await this.$axios.get(url, {
          params: { institution_user_id: student.id }
        })

        this.ledgerList = res.data || []
        if (!this.ledgerList.length) this.isLedgerNotFound = true
      } finally {
        this.isLoading = false
      }
    },

    async retrieveStudentLedger() {
      this.resetState()
      if (!this.searchKey.trim()) return

      this.isLoading = true
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/studentcategorymappings/view-ledger/student-list`
        const res = await this.$axios.get(url, { params: { search: this.searchKey } })
        this.students = res.data || [];
        if (!this.students.length) {
          this.isLedgerNotFound = true
        }
      } finally {
        this.isLoading = false
      }
    },

    resetState() {
      this.students = []
      this.ledgerList = []
      this.isLedgerNotFound = false
    },

    getGroupedTransactions(ledger) {
      const grouped = {}
      ledger?.fee_transactions?.forEach(tx => {
        const id = tx.fee_component.id
        if (!grouped[id]) grouped[id] = []
        grouped[id].push(tx)
      })
      return grouped
    },

    getTotal(arr, key) {
      return arr.reduce((sum, t) => sum + Number(t[key] || 0), 0)
    },

    formatAmount(amount) {
      return Number(amount || 0).toLocaleString('en-IN', {
        style: 'currency',
        currency: 'INR'
      })
    },

    formatDate(d) {
      if (!d) return ''
      const [y, m, day] = d.split('-')
      return `${day}/${m}/${y}`
    },

    getPaymentModeText(v) {
      return this.paymentModes.find(p => p.value === v)?.text || v
    },

    async downloadReceipt(feeCollectionId) {
      const url = `${this.$store.getters.getAPIKey.mainAPI}/feecollections/${feeCollectionId}/receipt`
      try {
        const response = await this.$axios.get(url, { responseType: 'blob' })
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = blobUrl
        link.setAttribute('download', `Receipt-${feeCollectionId}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(blobUrl)
      } catch (error) {
        this.$toast.error('Failed to download receipt. Please try again.')
      }
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.ledger-box {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  background: #fff;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}
</style>
